import { createSelector } from "reselect"
import { get, isEmpty, keyBy, size, find } from "lodash"
import { computeSelectedViews, computeSelectedViewsFactors, computeFactorsMetadata, mergeCategories } from "helpers/factors"
import { getUserPreference } from "helpers/userPreferences"
import { aggregateFactors } from "helpers/factorClassification/index"
import { createQueryStringSelector } from "selectors/queryString"
import { getModelFactorsClassification, getModelFactors, getModelCategories } from "selectors/modelMetadata"

const EMPTY_ARRAY = []
const EMPTY_OBJECT = {}

export const getRawSelectedViews = createQueryStringSelector("views", EMPTY_ARRAY)
const getRawFavoriteFactors = state => get(state, "global.data.userFavoriteFactors.data")
const getRawFactorCategory = createQueryStringSelector("factorCategory")

export const getFavoriteFactors = createSelector(
  [getRawFavoriteFactors],
  (favoriteFactors) => getUserPreference(favoriteFactors, "favoriteFactors", EMPTY_OBJECT),
)

export const getMustAggregateFactors = createSelector(
  [getModelFactorsClassification],
  (factorsClassification) => !isEmpty(factorsClassification),
)

const getSelectViewAggregatedFactorsMetadata = createSelector(
  [getModelFactors, getMustAggregateFactors, getModelFactorsClassification],
  (rawFactors, mustAggregateFactors, factorsClassification) => {
    if (!mustAggregateFactors) {
      return rawFactors
    }

    const factors = aggregateFactors(factorsClassification, rawFactors)
    return keyBy(factors, "id")
  },
)

export const getSelectedViews = createSelector(
  [getRawSelectedViews, getSelectViewAggregatedFactorsMetadata, getMustAggregateFactors],
  computeSelectedViews,
)

export const getSelectedViewsFactors = createSelector(
  [getSelectedViews, getModelFactors, getModelFactorsClassification],
  computeSelectedViewsFactors,
)

export const getCategories = createSelector(
  [getModelCategories, getSelectedViews],
  mergeCategories,
)

export const getFactorCategory = createSelector(
  [getRawFactorCategory, getCategories],
  (factorCategory, categories) => {
    if (factorCategory === "all") {
      return factorCategory
    } else if (find(categories, cat => cat.id === factorCategory)) {
      return factorCategory
    }

    return "all"
  },
)

/**
 * For factor trends, shouldAggregateFactors is set to false to display all the factors even if they can be aggregated.
 * It will also keep the custom views working if the model has factors that can be aggregated.
 * */
export const getFactorsMetadata = createSelector(
  [getModelFactors, getModelFactorsClassification, getSelectedViewsFactors],
  (rawFactors, modelFactorsClassification, selectedViewsFactors) =>
    computeFactorsMetadata(rawFactors, modelFactorsClassification, selectedViewsFactors, { shouldAggregateFactors: false }),
)

export const getDisplayAddView = createSelector(
  [getMustAggregateFactors, getSelectedViews],
  (mustAggregateFactors, selectedViews) => mustAggregateFactors && size(selectedViews) < 10,
)
